import React from 'react';
import PropTypes from 'prop-types';
import {useSelectedZone} from '@/services/geoblocking/hooks/useSelectedZone';
import {useGetCartQuery} from '@/services/api/cart-api';
import AdaptiveAspectImage from '@/components/SearchResults/components/global/AdaptiveAspectImage';
import AddToCartButton from '@/features/product/components/AddToCartButton';
import ContactButton from '@/features/product/components/ContactButton';
import {useFormatPrice} from '@/hooks/useFormatPrice';
import {CART_SOURCE_TYPES} from '@/services/api/api-constants';
import {mapLineItem} from '@/services/api/utils/cart-line-item-mapper';
import {selectCartId} from '@/features/cart/slices/cart-slice';
import {useSelector} from 'react-redux';
import ProductInCartCount from '@/features/product/components/ProductInCartCount';

const EbookCard = ({cardinfo, onViewDetailsClick, labels}) => {
  const zone = useSelectedZone();
  const cartId = useSelector(selectCartId);
  const {title, authors, image, shortDescription, longDescription, price, sku, eisbn} = cardinfo || {};
  const ebook = {
    title: title && Array.isArray(title) ? title.join(', ') : title,
    author: authors && Array.isArray(authors) ? authors.join(', ') : authors,
    image,
    shortDescription:
      shortDescription && Array.isArray(shortDescription) ? shortDescription.join(', ') : shortDescription,
    longDescription: longDescription && Array.isArray(longDescription) ? longDescription.join(', ') : longDescription,
    price,
    eisbn,
    sku,
  };
  const formattedPrice = useFormatPrice(ebook.price);
  const {data: cart} = useGetCartQuery(cartId, {skip: !cartId});
  const isPurchasable = Boolean(ebook.sku) && Boolean(ebook.price);
  const items = isPurchasable
    ? [mapLineItem(ebook, {sourceType: CART_SOURCE_TYPES.EBOOK_SEARCH_RESULT_ITEM, quantity: 1})]
    : null;
  return (
    <div className="ebook-card">
      <div className="ebook-card__cover-wrapper">
        {ebook.image && (
          <AdaptiveAspectImage
            className="ebook-card__cover"
            src={ebook.image}
            alt={ebook.title ? `${ebook.title} - ${labels.ebookCoverAlt}` : labels.ebookCoverAlt}
          />
        )}
      </div>
      <div className="ebook-card__details">
        <div className="ebook-card__meta">
          {ebook.title && <h3 className="ebook-card__title">{ebook.title}</h3>}
          {ebook.author && <p className="ebook-card__author">{`${labels.ebookByLabel} ${ebook.author}`}</p>}
          {ebook.shortDescription && <p className="ebook-card__description">{ebook.shortDescription}</p>}
        </div>
        <div className="ebook-card__details-actions">
          <calcite-link class="ebook-card__view-details" onClick={onViewDetailsClick}>
            {labels.ebookCardViewDetailsLabel}
          </calcite-link>
        </div>
      </div>
      <div className="ebook-card__pricing">
        <p className="ebook-card__price">{formattedPrice}</p>
        <div className="ebook-card__pricing-actions">
          <calcite-link class="ebook-card__view-details ebook-card__view-details--mobile" onClick={onViewDetailsClick}>
            {labels.ebookCardViewDetailsLabel}
          </calcite-link>
          {isPurchasable
            ? (
                <div className="ebook-card__add-to-cart-container">
                  <ProductInCartCount sku={ebook.sku} cartId={cart?._id} className="ebook-card__product-in-cart-count" />
                  <AddToCartButton className="ebook-card__add-to-cart" items={items} zone={zone} cartId={cart?._id}>
                    {labels.ebookAddToCartLabel}
                  </AddToCartButton>
                </div>
              )
            : (
                <ContactButton url={labels.ebookNoPriceLink}>{labels.ebookNoPriceLabel}</ContactButton>
              )}
        </div>
      </div>
    </div>
  );
};

EbookCard.propTypes = {
  cardinfo: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    authors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    image: PropTypes.string,
    shortDescription: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    longDescription: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    price: PropTypes.number,
    sku: PropTypes.number,
  }),
  onViewDetailsClick: PropTypes.func,
  labels: PropTypes.shape({
    ebookCoverAlt: PropTypes.string,
    ebookByLabel: PropTypes.string,
    ebookCardViewDetailsLabel: PropTypes.string,
    ebookAddToCartLabel: PropTypes.string,
    ebookNoPriceLabel: PropTypes.string,
    ebookNoPriceLink: PropTypes.string,
  }).isRequired,
};

export default EbookCard;
